import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Typography, Box, Divider} from 'views/components';
import {Translate, translate} from 'views/module';

import moment from 'moment';

const WorkHistoryView = (props) => {
  const {bioQuestions, setIsComplete} = props;

  const resolvedWorkHistory = React.useMemo(() => {
    const entries = Array.isArray(bioQuestions?.workHistory) ? bioQuestions.workHistory : [];

    return entries.reduce((accumulator, entry) => {
      const employerName = typeof entry?.employerName === 'string' ? entry.employerName.trim() : '';
      const startDate = entry?.startDate ? moment(entry.startDate).toISOString() : '';

      if (!employerName || !startDate) {
        return accumulator;
      }

      accumulator.push({
        employerName,
        startDate,
        endDate: entry?.endDate ? moment(entry.endDate).toISOString() : '',
        canContact: Boolean(entry?.canContact),
        referenceContactInfo: typeof entry?.referenceContactInfo === 'string' ? entry.referenceContactInfo.trim() : '',
      });

      return accumulator;
    }, []);
  }, [bioQuestions]);

  React.useEffect(() => {
    setIsComplete(resolvedWorkHistory.length > 0);
  }, [resolvedWorkHistory, setIsComplete]);

  const formatDateRange = React.useCallback((startDate, endDate) => {
    const start = startDate ? moment(startDate).format('MMM YYYY') : '';
    const end = endDate ? moment(endDate).format('MMM YYYY'): '';

    if (start && end) {
      return `${start} - ${end}`;
    }
    if (start) {
      const presentLabel = translate('caregiver.CaregiverBiography.WorkHistory.Summary.Present');
      return `${start} - ${presentLabel}`;
    }
    if (end) {
      const untilTemplate = translate('caregiver.CaregiverBiography.WorkHistory.Summary.UntilDate');
      return untilTemplate.replace('#{date}', end);
    }

    return translate('caregiver.CaregiverBiography.WorkHistory.Summary.DatesNotSpecified');
  }, []);

  if (resolvedWorkHistory.length === 0) {
    return (
      <Stack spacing={1.5} alignItems="flex-start">
        <Typography variant="body2" color="text.secondary">
          <Translate expression="caregiver.CaregiverBiography.WorkHistory.EmptyState" />
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5} alignItems="stretch" sx={{width: '100%'}}>
      <Typography variant="subtitle1" sx={{fontWeight: 600}}>
        <Translate
          expression="caregiver.CaregiverBiography.WorkHistory.Summary.Title"
          substitute={{
            count: resolvedWorkHistory.length,
            positions: translate(resolvedWorkHistory.length === 1 ?
                    'caregiver.CaregiverBiography.WorkHistory.Summary.PositionSingular' :
                    'caregiver.CaregiverBiography.WorkHistory.Summary.PositionPlural'),
          }}
        />
      </Typography>
      <Divider />
      {
        resolvedWorkHistory.map((entry, key) => {
          const referenceNote = entry.canContact ? ` • ${translate('caregiver.CaregiverBiography.WorkHistory.Summary.ReferenceAvailable')}` : '';
          return (
            <Box
              key={key}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #0288d1',
                borderRadius: '12px',
                backgroundColor: '#e1f5fe',
                paddingY: 1,
                paddingX: 1.5,
                gap: 0.5,
              }}
            >
              <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 600}}>
                {entry.employerName}
              </Typography>
              <Typography variant="body2" sx={{color: '#0288d1'}}>
                {`${formatDateRange(entry.startDate, entry.endDate)}${referenceNote}`} {` `}
                {
                  entry.referenceContactInfo &&
                  entry.referenceContactInfo
                }
              </Typography>
            </Box>
          );
        })
      }
    </Stack>
  );
};

WorkHistoryView.propTypes = {
  bioQuestions: PropTypes.object.isRequired,
  setIsComplete: PropTypes.func.isRequired,
};

export default React.memo(WorkHistoryView);
